import { createContext, ReactNode, useEffect } from "react";
import {
  Authenticator as AmplifyAuthenticator,
  ThemeProvider,
  useAuthenticator as useAmplifyAuthenticator,
  useTheme,
} from "@aws-amplify/ui-react";
import { signOut as amplifySignOut } from "@aws-amplify/auth";

import { WebTheme } from "@aws-amplify/ui/dist/types/theme/types";
import ToasterOven from "../unhealthie/ToasterOven";
import { Navigate } from "react-router-dom";
import PageLoadingSpinner from "../unhealthie/PageLoadingSpinner";
import { generateRedirectingLoginUrl } from "../urlUtils";

interface AuthenticatorProps {
  // Legacy Amplify Authenticator props
  theme: WebTheme;
  children: (signOut?: () => void) => JSX.Element;

  // Our custom props, if any, can go here.
}

// const LOCALSTORAGE_KEY = `code_challenge_authentication_override_${process.env.REACT_APP_FAY_APP}`;

type FayAuthContextType = {
  signOut: () => Promise<void>;
  isLoggedIn: boolean;
};

export const FayAuthContext = createContext<FayAuthContextType>({
  isLoggedIn: false,
  signOut: async () => {},
});

interface FayAuthProviderProps {
  children: (signOut?: () => void) => ReactNode;
}

export const FayAuthProvider = ({ children }: FayAuthProviderProps) => {
  const { authStatus, isPending } = useAmplifyAuthenticator((context) => [
    context.authStatus,
    context.isPending,
  ]);

  const isLoggedIn = authStatus === "authenticated";
  const isConfiguring = authStatus === "configuring";
  // Check if the route is /login
  const isAtLogin = window.location.pathname === "/login";
  const isAtResetPassword = window.location.pathname === "/reset-password";
  const isLoginExempt = isAtLogin || isAtResetPassword;

  const signOut = async () => {
    await amplifySignOut();
  };

  return (
    <FayAuthContext.Provider value={{ signOut, isLoggedIn }}>
      {isPending || isConfiguring ? (
        <PageLoadingSpinner />
      ) : isLoggedIn || isLoginExempt ? (
        children(signOut)
      ) : (
        <Navigate to={generateRedirectingLoginUrl()} />
      )}
    </FayAuthContext.Provider>
  );
};

const Authenticator = ({ theme, children }: AuthenticatorProps) => {
  // Only worry about the below once we start testing PROVIDER auth
  // const [params, setParams] = useSearchParams();
  //
  // const [
  //   codeChallengeAuthenticationOverride,
  //   setCodeChallengeAuthenticationOverride,
  // ] = useLocalStorage<boolean>(LOCALSTORAGE_KEY, false);
  //
  // useEffect(() => {
  //   // If the user has the query parameter `$LOCALSTORAGE_KEY` set to `1`, we will set the localStorage item to `true`.
  //   const paramValue = params.get(LOCALSTORAGE_KEY);
  //
  //   if (paramValue !== null) {
  //     if (paramValue === "1") {
  //       setCodeChallengeAuthenticationOverride(true);
  //     } else if (paramValue === "0") {
  //       setCodeChallengeAuthenticationOverride(false);
  //     }
  //
  //     const newParams = new URLSearchParams(params);
  //     newParams.delete(LOCALSTORAGE_KEY);
  //     setParams(newParams);
  //   }
  // }, [params, setParams, setCodeChallengeAuthenticationOverride]);

  // New auth is only going to be enabled on CLIENT experiences for now.
  // When we launch it for providers, we will only render `FayAuthProvider`
  // in all conditions below.
  const useNewAuth = process.env.REACT_APP_FAY_APP === "client";

  return (
    <>
      {useNewAuth ? (
        <FayAuthProvider>{children}</FayAuthProvider>
      ) : (
        <FayAmplifyAuthenticator theme={theme}>
          {children}
        </FayAmplifyAuthenticator>
      )}
      <ToasterOven />
    </>
  );
};

const FayAmplifyAuthProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { signOut, route, authStatus } = useAmplifyAuthenticator((context) => [
    context.signOut,
    context.route,
    context.authStatus,
  ]);

  useEffect(() => {
    if (authStatus === "authenticated" && route === "signIn") {
      // This is some kind of wacky bug or symptom of misuse of Amplify
      // components. If we're authenticated but the route is signIn, then
      // we are "stuck" and we need to refresh the page to get out of this.
      window.location.reload();
    }
  }, [authStatus, route]);

  return (
    <FayAuthContext.Provider
      value={{
        signOut: async () => signOut(),
        isLoggedIn: route === "authenticated",
      }}
    >
      {children}
    </FayAuthContext.Provider>
  );
};

interface FayAmplifyAuthenticatorProps {
  theme: WebTheme;
  children: (signOut?: () => void) => JSX.Element;
}

const FayAmplifyAuthenticator = ({
  theme,
  children,
}: FayAmplifyAuthenticatorProps) => {
  const authenticatorComponents = {
    Header() {
      const { tokens } = useTheme();
      // Our Cognito pool uses case-sensitive usernames, and converting to
      // case-insensitive usernames requires migrating to a new user pool.
      // To work around this, we add the ForceUsernameToLowerCase to convert
      // user-entered emails to lowercase.
      useEffect(() => forceUsernameToLowerCase());

      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: tokens.space.large.value,
          }}
        >
          <img alt="Fay Nutrition" src="/logo.png" width="217px" />
        </div>
      );
    },
  };

  return (
    <FayAmplifyAuthProvider>
      <ThemeProvider theme={theme}>
        <AmplifyAuthenticator
          formFields={{
            signIn: {
              username: {
                placeholder: "Enter your Email",
                isRequired: true,
                label: "Email",
              },
            },
            forgotPassword: {
              username: {
                placeholder: "Enter your Email",
                isRequired: true,
                label: "Enter your Email",
              },
            },
          }}
          hideSignUp={true}
          components={authenticatorComponents}
          variation="modal"
        >
          {({ signOut }) => children(signOut)}
        </AmplifyAuthenticator>
      </ThemeProvider>
    </FayAmplifyAuthProvider>
  );
};

const forceUsernameToLowerCase = () => {
  const usernameInput: HTMLInputElement | null = document.querySelector(
    'input[name="username"]',
  );

  if (usernameInput) {
    const listener: EventListenerOrEventListenerObject = () => {
      if (usernameInput) {
        // Restore the cursor location so that it doesn't jump when we edit
        // the middle of the input
        const [start, end] = [
          usernameInput.selectionStart,
          usernameInput.selectionEnd,
        ];
        usernameInput.value = usernameInput?.value?.toLowerCase();
        usernameInput.setSelectionRange(start, end);
      }
    };

    usernameInput.addEventListener("input", listener);

    // Clean up the event listener when the component unmounts
    return () => {
      usernameInput.removeEventListener("input", listener);
    };
  }
};

export default Authenticator;
